/*
 * When we navigate between two points (using a shortest-path algorithm), the result is an array of WayfindingSegments.
 *
 * Each WayfindingSegment includes a line connecting two portals on the map, along with additional
 * information (typically including badges) necessary to display it on the map and other windows in the UI
 *
 * A WayfindingSegment must conform to the schema in wayfinding-segment.json
 * Use WayfindingSegment.from or fromArray, to validate that your data conforms
 */
import { map, prepend } from 'ramda'
import JsonSchemaTools from './json-schema-tools'

const { validate, from } = JsonSchemaTools('WayfindingSegment')

/*
 * Convert line data from a WayfindingSegment into a GeoJSON LineString
 * @sig :: WayfindingSegment -> GeoJsonFeature
 */
const _toLineStringGeoJson = wayfindingSegment => ({
    type      : 'Feature',
    geometry  : {
        type       : 'LineString',
        coordinates: wayfindingSegment.coordinates,
    },
    properties: {
        segmentType: wayfindingSegment.segmentType,
        venueId    : wayfindingSegment.venueId,
        ordinalId  : wayfindingSegment.ordinalId,
    },
})

/*
 * Convert badge data (for a single badge) from a WayfindingSegment into a GeoJSON Point
 * @sig :: WayfindingSegment -> GeoJsonFeature
 */
const _toBadgeGeoJson = wayfindingSegment => badge => ({
    type      : 'Feature',
    geometry  : {
        type       : 'Point',
        coordinates: badge.coordinates,
    },
    properties: {
        badge    : badge.canonicalName,
        entityId : badge.entityId,
        venueId  : wayfindingSegment.venueId,
        ordinalId: wayfindingSegment.ordinalId,
    },
})

/*
 * Convert a WayfindingSegment to an array of GeoJSON Features, one for the line and one for each badge
 * @sig toGeoJSON :: WayfindingSegment ~> [GeoJsonFeature]
 */
const toGeoJson = wayfindingSegment => {
    const line   = _toLineStringGeoJson(wayfindingSegment)
    const badges = map(_toBadgeGeoJson(wayfindingSegment), wayfindingSegment.badges)

    return prepend(line, badges)
}

/*
 * Create an array of validated WayfindingSegments from an array of POJO's
 * @sig fromArray :: [{k: v}] -> [WayfindingSegment]
 */
const fromArray = pojos => map(from, pojos)

export {
    validate,
    from,
    fromArray,
    toGeoJson,
}

